/**
 * History Lens (Archive Search + Contract Timeline) - the shared, testable parts.
 *
 * Enrichment used to load the WHOLE airport_contracts (~49k), contracts (~11k) and Invygo collections
 * as soon as either tab opened, then ran .find() over all of them for every result row (26 Sep 2026
 * audit, CON-07). Now only the contract numbers of the rows found are read (by id, 30 per query), at
 * most MAX_LOOKUP of them, and indexed in Maps. Invygo's own booking id of the same contracts comes from
 * invygoIds (the one path: Ejar's booking -> broker reference; the old sheet's history for old contracts - the
 * sheet is no longer uploaded, owner 28 Sep 2026).
 */
import { airportService, contractsService } from '../../../services/firestore.js';
import { invygoIdsFor } from '../../../services/invygoIds';
import { echeckCorrections } from '../../../services/echeckCorrections';
import { parseSheetDate } from '../../MultiCar/utils/dates';

/** A search that matches thousands of contracts enriches the newest MAX_LOOKUP (the rest keep the E-Check name). */
export const MAX_LOOKUP = 300;

export const contractKey = (v) => String(v || '').toUpperCase().trim();
export const plateKey = (v) => String(v || '').toUpperCase().trim().replace(/\s+/g, '');

/**
 * Ejar's E-Check number (the store's check id when there is none): the order of two checks in the SAME minute - the
 * archive keeps minutes only. Owner, 3 Oct 2026 (CC 79361): I2608000D01410485 was created on CC 79361 at 17:37:19 and
 * closed on it at 17:37:59 (the wrong car, put right); both read "17:37", the Close was taken first and the car looked
 * never returned - the timeline and the day's search showed two contracts on one car. Ejar numbers checks in order.
 */
export const checkSeq = (r) => {
    const n = Number(String(r?.['E-Check Number'] ?? '').trim());
    if (Number.isFinite(n) && n > 0) return n;
    const id = Number(r?.check_id);
    return Number.isFinite(id) ? id : 0;
};

/** A plate for comparing what was typed with a record: letters and digits only ("W-28656" = "W 28656" = "w28656"). */
export const plateMatchKey = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/** The car a search was for: the searched text when it is the plate of a record found ('' for a name or a contract). */
export const searchedPlate = (term, records) => {
    const k = plateMatchKey(term);
    return k && (records || []).some((r) => plateMatchKey(r['Plate Number']) === k) ? k : '';
};

export const EMPTY_ENRICHMENT = Object.freeze({ airport: new Map(), contracts: new Map(), invygo: new Map() });

/**
 * Rows -> Maps by contract number. The first row of a key wins (as .find() did). `invygo` is invygoIdsFor's
 * Map(CONTRACT NO -> { invygoId, source, customerName?, phone? }) - by contract, never by plate: a car serves many
 * customers, so a plate named the Invygo booking of whoever had the car last.
 */
export const indexEnrichment = ({ airport = [], contracts = [], invygo = new Map() } = {}) => {
    const put = (map, key, row) => { if (key && !map.has(key)) map.set(key, row); };
    const idx = { airport: new Map(), contracts: new Map(), invygo: new Map() };
    airport.forEach((d) => put(idx.airport, contractKey(d.contract_no || d['Contract No.'] || d.id), d));
    contracts.forEach((c) => put(idx.contracts, contractKey(c['Contract No.'] || c.contract_no || c.id), c));
    (invygo instanceof Map ? [...invygo] : []).forEach(([no, v]) => put(idx.invygo, contractKey(no), v));
    return idx;
};

/** The airport / Business Bay contract and the Invygo booking of one contract. */
export const enrichmentFor = (index, contractNo) => ({
    airportMatch: index.airport.get(contractKey(contractNo)) || null,
    ejarMatch: index.contracts.get(contractKey(contractNo)) || null,
    invygoMatch: index.invygo.get(contractKey(contractNo)) || null,
});

/** Said on the page when only the Invygo booking ids could not be read (the other details did load). */
export const INVYGO_IDS_FAILED = 'Invygo booking IDs could not be loaded - an Invygo contract shows Ejar\'s own booking number instead. Search again to retry.';

/**
 * Read the enrichment of these contract numbers (at most MAX_LOOKUP) plus the saved E-Check
 * corrections of the same contracts. A failed read of the contracts / airport contracts / corrections
 * throws (the caller shows it) - never an empty result that looks like "no customer found".
 * The Invygo ids are read on their own (review, 28 Sep 2026): the old sheet's read it replaced never
 * failed, and none of the customers, phones or bookings depends on Invygo - so a failed Invygo read
 * keeps what loaded and comes back as `invygoError` (its message) with no Invygo ids.
 */
export const loadHistoryEnrichment = async (contractNos, { withCorrections = false } = {}) => {
    const ids = [...new Set((contractNos || []).map(contractKey).filter(Boolean))].slice(0, MAX_LOOKUP);
    if (!ids.length) return { index: EMPTY_ENRICHMENT, corrections: new Map(), ids, invygoError: null };
    let invygoError = null;
    const [airport, contracts, invygo, corrections] = await Promise.all([
        airportService.getAirportContractsByIds(ids),
        contractsService.getContractRowsByIds(ids),
        invygoIdsFor(ids).catch((e) => { invygoError = (e && e.message) || 'Invygo booking IDs not read'; return new Map(); }),
        withCorrections ? echeckCorrections.forContracts(ids) : Promise.resolve(new Map()),
    ]);
    return { index: indexEnrichment({ airport, contracts, invygo }), corrections, ids, invygoError };
};

/**
 * The chosen day (the date input's "YYYY-MM-DD") as a LOCAL (Dubai) window, like the records, which
 * parseSheetDate reads as local times. It was built with Date.UTC, so in Dubai the window ran from
 * 04:00 to 03:59 the next day (CON-09).
 */
export const dayWindow = (yyyyMmDd) => {
    const [y, m, d] = String(yyyyMmDd || '').split('-').map(Number);
    if (!y || !m || !d) return null;
    const start = new Date(y, m - 1, d).getTime();
    const end = new Date(y, m - 1, d + 1).getTime() - 1;
    return { start, end };
};

const ts = (v) => (v instanceof Date ? v.getTime() : v);

/** A contract session [start, end] touches the window. */
export const overlapsWindow = (item, win) => !win || (ts(item._start) <= win.end && ts(item._end) >= win.start);

/**
 * The contract numbers worth reading for a timeline: with a day chosen, only the sessions near that day
 * (E-Check dates, widened by `marginDays` because Ejar's dates can differ a little); newest first.
 */
export const lookupIdsFor = (items, win, marginDays = 3) => {
    const pad = marginDays * 86400000;
    const wide = win ? { start: win.start - pad, end: win.end + pad } : null;
    const seen = new Set();
    [...items].sort((a, b) => ts(b._start) - ts(a._start)).forEach((it) => {
        if (overlapsWindow(it, wide)) seen.add(contractKey(it.contractNo));
    });
    return [...seen].filter(Boolean).slice(0, MAX_LOOKUP);
};

/**
 * The E-Check corrections to write for these timeline rows (CON-08): only contracts whose saved
 * correction was actually read (`checkedIds`), not already written this session (`written` holds
 * fingerprints), whose Ejar dates differ from the E-Check ones; at most `cap` per search.
 */
export const correctionsToSave = (results, { checkedIds, written, fpOf, cap = 50 }) => {
    const todo = new Map();
    results.forEach((it) => {
        const t = todo.get(it._key) || { contract: it._key };
        if (it.fromEjar === 'live') { t.from = it.from; t.echeckFrom = it._echeckFrom; }
        if (it.toEjar === 'live') { t.to = it.to; t.echeckTo = it._echeckTo; }
        todo.set(it._key, t);
    });
    const out = [];
    todo.forEach((t, key) => {
        if (out.length >= cap) return;
        if (!checkedIds.has(key)) return;
        if (!t.from && !t.to) return;
        if (t.from === t.echeckFrom && (t.to || '') === (t.echeckTo || '')) return;   // nothing to correct
        if (written.has(fpOf(key, t.from, t.to))) return;
        out.push(t);
    });
    return out;
};

/** "dd/mm/yyyy HH:MM" of the E-Check archive -> local ms (0 when unreadable). */
export const archiveTime = (value) => {
    const d = parseSheetDate(value);
    return d ? d.getTime() : 0;
};

/**
 * Archive Search "Action Date": per contract, the car it had on that day - the contiguous block of
 * records of the plate in use then; a contract already closed before the day is left out. Applied
 * to the rows on screen whenever the date changes (it used to run only inside Fetch Records - CON-20).
 */
export const snapshotOnDay = (records, yyyyMmDd) => {
    const win = dayWindow(yyyyMmDd);
    if (!win) return records;
    const groups = new Map();
    records.forEach((log) => {
        const cNo = log['Contract No.'];
        if (!cNo) return;
        if (!groups.has(cNo)) groups.set(cNo, []);
        groups.get(cNo).push(log);
    });
    let out = [];
    groups.forEach((list) => {
        const logs = [...list].sort((a, b) => (archiveTime(a['Transaction Date']) - archiveTime(b['Transaction Date']))
            || (checkSeq(a) - checkSeq(b)));
        let at = -1;
        for (let i = logs.length - 1; i >= 0; i--) {
            if (archiveTime(logs[i]['Transaction Date']) <= win.end) { at = i; break; }
        }
        if (at === -1) return;
        const log = logs[at];
        const type = String(log['Check Type'] || '').toLowerCase();
        const isClosed = type.includes('close') || type.includes('cancel');
        if (isClosed && archiveTime(log['Transaction Date']) < win.start) return;   // returned before the day
        const plate = log['Plate Number'];
        let from = at;
        let to = at;
        while (from > 0 && logs[from - 1]['Plate Number'] === plate) from--;
        while (to < logs.length - 1 && logs[to + 1]['Plate Number'] === plate) to++;
        out = out.concat(logs.slice(from, to + 1));
    });
    return out;
};

/** One CSV cell: quoted when it holds a comma, quote or line break. */
export const csvCell = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (headers, rows) => [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');

/**
 * Contract Timeline: E-Check records -> one session per contract + car (opening record, then the
 * closing record of the same contract and plate; a switch to another plate ends the search).
 */
export const groupSessions = (records) => {
    const data = (records || []).map((r) => ({ ...r, _parsedDate: parseSheetDate(r['Transaction Date']) }))
        .filter((r) => r._parsedDate !== null);
    data.sort((a, b) => (a._parsedDate - b._parsedDate) || (checkSeq(a) - checkSeq(b)));

    const items = [];
    const processed = new Set();
    data.forEach((record, index) => {
        if (processed.has(index)) return;
        const type = (record['Check Type'] || '').toLowerCase();
        if (!(type.includes('create') || type.includes('open'))) return;
        const contractNo = record['Contract No.'] || 'Unknown';
        const plate = record['Plate Number'] || 'Unknown';
        let closeRecord = null;
        for (let i = index + 1; i < data.length; i++) {
            const next = data[i];
            const nextType = (next['Check Type'] || '').toLowerCase();
            const isClosing = nextType.includes('close') || nextType.includes('cancel');
            if (isClosing && next['Contract No.'] === contractNo && next['Plate Number'] === plate) {
                closeRecord = next;
                processed.add(i);
                break;
            }
            if (next['Contract No.'] === contractNo && next['Plate Number'] !== plate) break;
        }
        processed.add(index);
        items.push({
            _key: contractKey(record['Contract No.']),
            contractNo: record['Contract No.'],
            plate,
            recordCustomer: record['Customer Name'],
            model: record['Model'],
            branch: record['Branch'],
            from: record['Transaction Date'],
            to: closeRecord ? closeRecord['Transaction Date'] : 'Still Open',
            _start: record._parsedDate,
            _end: closeRecord ? closeRecord._parsedDate : Date.now(),
        });
    });
    return items;
};

/**
 * Sessions + enrichment -> the timeline rows: customer / booking from the contract found, Ejar's
 * pick-up / drop-off over the E-Check times (or the correction saved earlier), then the day filter
 * (a local day - CON-09); newest first. The rules are the page's as before; only the reads moved.
 */
export const timelineRows = (sessions, { index = EMPTY_ENRICHMENT, corrections = new Map(), filterDate = '' } = {}) => {
    const items = sessions.map((s) => {
        const { airportMatch, ejarMatch, invygoMatch } = enrichmentFor(index, s._key);
        const customerName = airportMatch?.Customer || airportMatch?.['Customer Name'] || ejarMatch?.Customer || invygoMatch?.customerName || s.recordCustomer || 'Unknown Customer';
        const bookingNo = invygoMatch?.invygoId || airportMatch?.booking_number || ejarMatch?.['Booking Number'] || 'N/A';
        const ejarDoc = airportMatch || ejarMatch || null;
        const { recordCustomer, ...rest } = s;
        return {
            ...rest,
            customerName,
            bookingNo,
            _ejar: ejarDoc ? {
                pickup: ejarDoc['Pick-up Date'] || ejarDoc.pickup_date || '',
                dropoff: ejarDoc['Drop-off Date'] || ejarDoc.dropoff_date || '',
                // Ejar: Delivered-Paid / Delivered-Indebt / Closed = returned; Cancelled ends it too
                closed: /closed|delivered|returned|completed|cancel/i.test(String(ejarDoc['Status'] || ejarDoc.status || '')),
            } : null,
        };
    });

    // Whichever came first (owner, 6 Oct 2026: "ايهما الاول يعتمد عليه"): the first car's "From" = the contract's
    // pick-up when it is before the first E-Check (Ejar opens first), the last car's "To" = Ejar's drop-off once Ejar
    // has it closed when it is before the last close check (or there is none); car switches in between keep the
    // E-Check times. No Ejar record loaded: the correction saved earlier (echeck_corrections), by the same rule.
    const byContract = new Map();
    items.forEach((it) => { if (!byContract.has(it._key)) byContract.set(it._key, []); byContract.get(it._key).push(it); });
    byContract.forEach((list, key) => {
        list.sort((a, b) => a._start - b._start);
        const first = list[0];
        const last = list[list.length - 1];
        first._echeckFrom = first.from;
        last._echeckTo = last.to;
        const ej = first._ejar;
        const saved = corrections.get(key);
        const fromText = ej ? ej.pickup : (saved && saved.from);
        const toText = ej ? (ej.closed ? ej.dropoff : '') : (saved && saved.to);
        const pick = fromText ? parseSheetDate(fromText) : null;
        if (pick && pick <= first._start) { first.from = String(fromText); first._start = pick; first.fromEjar = ej ? 'live' : 'saved'; }
        const drop = toText ? parseSheetDate(toText) : null;
        if (drop && (last.to === 'Still Open' || drop <= last._end) && drop >= last._start) {
            last.to = String(toText); last._end = drop; last.toEjar = ej ? 'live' : 'saved';
        }
    });

    const win = filterDate ? dayWindow(filterDate) : null;
    return items.filter((it) => overlapsWindow(it, win)).sort((a, b) => b._start - a._start);
};
